// client/src/context/GeoContext.js
// Detects a visitor's actual country by IP (not by browser language, which a
// VPN or a foreign-language OS setting doesn't reliably reflect), and derives
// from that: which language to auto-translate to, and what currency to show
// estimated prices in. Deliberately NOT cached across page loads - a
// visitor's apparent location can genuinely change (VPN, travel, switching
// networks), and serving yesterday's country for up to a day would silently
// ignore that. It's one small request per full page load either way.
import React, { createContext, useContext, useEffect, useState } from 'react';

// Country -> ISO 4217 currency code. Deliberately not exhaustive - anything
// missing just falls back to ZAR (no conversion shown), which is always safe.
const COUNTRY_CURRENCY = {
  ZA: 'ZAR', LS: 'ZAR', NA: 'ZAR', SZ: 'ZAR', // currencies pegged to / used alongside ZAR
  KE: 'KES', TZ: 'TZS', UG: 'UGX', NG: 'NGN', GH: 'GHS', EG: 'EGP',
  ZW: 'USD', BW: 'BWP', MZ: 'MZN', ZM: 'ZMW',
  US: 'USD', GB: 'GBP', IE: 'EUR',
  FR: 'EUR', DE: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', PT: 'EUR', BE: 'EUR', AT: 'EUR',
  CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN',
  CA: 'CAD', AU: 'AUD', NZ: 'NZD',
  IN: 'INR', CN: 'CNY', JP: 'JPY', KR: 'KRW', SG: 'SGD', AE: 'AED', SA: 'SAR',
  BR: 'BRL', MX: 'MXN', AR: 'ARS',
};

// Country -> Google Translate target language code. Kept in sync with
// GoogleTranslateWidget's own supported-language allowlist.
const COUNTRY_LANG = {
  KE: 'sw', TZ: 'sw',
  FR: 'fr', BE: 'fr', CI: 'fr', SN: 'fr', CD: 'fr', CM: 'fr',
  DE: 'de', AT: 'de', CH: 'de',
  ES: 'es', MX: 'es', AR: 'es',
  PT: 'pt', BR: 'pt', MZ: 'pt', AO: 'pt',
  NL: 'nl', IT: 'it', CN: 'zh-CN', SA: 'ar', AE: 'ar', EG: 'ar', IN: 'hi',
  LS: 'st', SZ: 'ss',
};

const GeoContext = createContext({
  ready: false,
  countryCode: null,
  currencyCode: 'ZAR',
  targetLanguage: null,
  formatLocalPrice: () => null,
});

export const GeoProvider = ({ children }) => {
  const [geo, setGeo] = useState({
    ready: false,
    countryCode: null,
    currencyCode: 'ZAR',
    targetLanguage: null,
    rates: null,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const countryRes = await fetch('https://get.geojs.io/v1/ip/country.json', { cache: 'no-store' });
        const countryData = await countryRes.json();
        const countryCode = countryData.country || null;

        const currencyCode = COUNTRY_CURRENCY[countryCode] || 'ZAR';
        const targetLanguage = COUNTRY_LANG[countryCode] || null;

        let rates = null;
        if (currencyCode !== 'ZAR') {
          const ratesRes = await fetch('https://open.er-api.com/v6/latest/ZAR', { cache: 'no-store' });
          const ratesData = await ratesRes.json();
          if (ratesData.result === 'success') rates = ratesData.rates;
        }

        setGeo({ countryCode, currencyCode, targetLanguage, rates, ready: true });
      } catch {
        // Geolocation is a nice-to-have - on any failure (network, ad
        // blocker, offline) the site just stays in its default ZAR/English.
        setGeo((g) => ({ ...g, ready: true }));
      }
    };
    load();
  }, []);

  const formatLocalPrice = (zarAmount) => {
    if (!geo.rates || geo.currencyCode === 'ZAR') return null;
    const rate = geo.rates[geo.currencyCode];
    if (!rate) return null;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: geo.currencyCode })
        .format(Number(zarAmount || 0) * rate);
    } catch {
      return null;
    }
  };

  return (
    <GeoContext.Provider value={{ ...geo, formatLocalPrice }}>
      {children}
    </GeoContext.Provider>
  );
};

export const useGeo = () => useContext(GeoContext);
